import { useUpdateAnalysis } from '@modules/analysis/hooks/useUpdateAnalysis';
import { AnalysisStoreOptions } from '@modules/analysis/store/types';
import { ArchiveArtefactConfirmDialog } from '@modules/common/components/ArchiveArtefactConfirmDialog';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { NAV_PATHS } from '@modules/common/constants';
import { ArtefactType } from '@modules/common/types';
import { getAnalysisTypeDisplayName } from '@modules/dashboard/helpers';
import { AnalysisUseCase, Status } from 'generated/graphql';
import { useNavigate } from 'react-router-dom';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { mapFromEdgeScope } from 'v2/core/repository/edge-server/analysis/fromEdgeAnalysis';
import { useGetUser } from 'v2/core/services/user';

const trackAnalysisArchiveEvents = createAnalyticsTracker('analysisV3Archive');

interface ArchiveAnalysisFormProps {
  queryMetadata: string;
  analysis: AnalysisStoreOptions;
}

export const ArchiveAnalysisForm = ({
  analysis,
  queryMetadata,
}: ArchiveAnalysisFormProps) => {
  const { closeModal } = useMuiModalContext();

  const { id, title, useCase, editToken, createdBy, scope, href } = analysis;

  const {
    tenant: { id: tenantID },
  } = useGetUser();
  const { updateAnalysis } = useUpdateAnalysis({
    name: title,
    scope: mapFromEdgeScope(scope),
    id: id,
    editToken: editToken,
    createdBy: createdBy,
    metadata: queryMetadata,
    status: Status.Archived,
  });
  const { showSuccessNotification, showErrorNotification } =
    useNotificationContext();

  const navigate = useNavigate();

  const onArchive = async () => {
    try {
      await updateAnalysis({
        variables: {
          title,
          scope,
          id,
          editToken: editToken ?? '',
          createdBy: createdBy?.id,
          tenantID,
          metadata: queryMetadata,
          status: Status.Archived,
        },
      });
      navigate(
        analysis.useCase === AnalysisUseCase.Feed
          ? `/${NAV_PATHS.FEED}`
          : `/${NAV_PATHS.QUANTIFY}`,
      );
      showSuccessNotification({
        contentComponent: `This ${getAnalysisTypeDisplayName(
          analysis.useCase,
        )} is archived.`,
        props: { isClosable: true },
      });
      trackAnalysisArchiveEvents({
        eventName: 'Analysis Archive',
        name: title,
        link: href,
        status: Status.Archived,
        useCase,
      });
      closeModal();
    } catch {
      showErrorNotification({
        contentComponent: `Failed to archive ${getAnalysisTypeDisplayName(
          analysis.useCase,
        )}.`,
        props: { isClosable: true },
      });
    }
  };

  return (
    <ArchiveArtefactConfirmDialog
      onArchive={onArchive}
      artefactTitle={title}
      artefactType={
        useCase === AnalysisUseCase.Default
          ? ArtefactType.Quantify
          : ArtefactType.Feed
      }
    />
  );
};
